import {
    FC,
    useEffect,
    useContext,
    useState,
} from "react";
import { Button, Dropdown, Modal } from "react-bootstrap";
import { VIEW_TYPE, XML_TAB_CONFIG } from "./constants";
import CustomTabMenu from "../../../component/CustomTabMenu";
import XmlViewerComponent from "./XmlViewerComponent";
import { IconBack } from "../../../component/IconSvg";
import { usePhanHeBaoHiemYTeContext, useXMLTabContext } from "../../contexts";
import { CODE, RESPONSE_MESSAGE } from "../../../utils/Constant";
import { gettXmlTabs, validateXML, exportInsuranceXML, downloadXML, exportCheckinXML, sendXMLPayment, sendXMLCheckIn } from "../../services";
import { AppContext } from "../../../appContext/AppContext";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { getFileExportName } from "../../../utils/AppFunction";

interface Props {
    handleClose: () => void;
}

const XmlPreview: FC<Props> = (props) => {
    const  { handleClose } = props;
    const { patientInfo } = usePhanHeBaoHiemYTeContext();
    const { setIsLoading } = useContext(AppContext);
    const { setValidationErrors, setViewType, viewType } = useXMLTabContext();

    const [dsTabs, setDsTabs] = useState<any[]>([]);
    const [checkExport, setCheckExport] = useState<boolean>(false);

    const handleUpdateTabs = async () => {
		try {
			const { data, code } = await gettXmlTabs({
				patientCode: patientInfo?.patientCode,
				visitCode: patientInfo?.visitCode,
			});

			if (CODE.SUCCESS === code && data) {
				const tabs = (Array.isArray(data) ? data : [data])
					.filter((item: string) => XML_TAB_CONFIG?.[item])
					.map((item: string) => ({
						title: XML_TAB_CONFIG?.[item].title,
						component: <XmlViewerComponent xmlTabConfig={XML_TAB_CONFIG?.[item]} />,
					}));
				setDsTabs(tabs);
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		}
	};

    useEffect(() => {
        handleUpdateTabs();
    }, [checkExport]);

    const onExportXML = async () => {
        try {
            setIsLoading(true);
            const data = await exportInsuranceXML({
                patientCode: patientInfo?.patientCode,
                visitCode: patientInfo?.visitCode,
            });

            if (data) {
                setCheckExport(!checkExport);
                toast.success("Xuất file XML thành công.");
            }
			setValidationErrors({});
        } catch (error) {
            console.error(error);
            toast.error(RESPONSE_MESSAGE.ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    const onValidateXML = async () => {
		try {
			setIsLoading(true);
			const { code, data } = await validateXML({
				patientCode: patientInfo?.patientCode,
				visitCode: patientInfo?.visitCode,
			});

			if (CODE.SUCCESS === code) {
                setValidationErrors(data);
				toast.success('Validate XML thành công');
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
            setIsLoading(false);
        }
	};

	const onDownloadXML = async () => {
		try {
			setIsLoading(true);
			const { headers, data } = await downloadXML({
				patientCode: patientInfo?.patientCode,
				visitCode: patientInfo?.visitCode,
			});
			const fileName = getFileExportName(headers);
			saveAs(
				new Blob([data], {
					type: 'application/xml',
				}),
				fileName
			);
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const onExportCheckinXML = async () => {
        try {
            setIsLoading(true);
            const data = await exportCheckinXML({
                patientCode: patientInfo?.patientCode,
                visitCode: patientInfo?.visitCode,
            });

            if (data) {
                setCheckExport(!checkExport);
                toast.success("Xuất file XML thành công.");
            }
			setValidationErrors({});
        } catch (error) {
            console.error(error);
            toast.error(RESPONSE_MESSAGE.ERROR);
        } finally {
            setIsLoading(false);
        }
    };

	const onSendXML = async (type: 'checkin' | 'payment') => {
        try {
            setIsLoading(true);
			const params = {
				patientCode: patientInfo?.patientCode,
				visitCode: patientInfo?.visitCode,
			};
			const isSendCheckIn = type === 'checkin';

            const { data } = isSendCheckIn
				? await sendXMLCheckIn(params)
				: await sendXMLPayment(params);

			const { maKetQua, thongDiep } = data;

			if (CODE.SUCCESS.toString() === maKetQua) {
				toast.success(thongDiep);
				return;
			} 

			toast.error(thongDiep);
        } catch (error) {
            console.error(error);
            toast.error(RESPONSE_MESSAGE.ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    return (
		<Modal
			className="page-full"
			fullscreen
			dialogClassName="h-modal"
			size="xl"
			show={true}
			animation={false}
			centered
			backdropClassName="spaces top-50"
		>
			<div className="h-100">
				<Modal.Header className="header-modal d-flex flex-wrap">
					<div className="d-flex gap-2 pb-1 pt-2">
						<div
							className="d-flex align-items-center gap-3 mr-20 cursor-pointer"
							onClick={handleClose}
						>
							<IconBack />
							<span className="fw-500">Quay lại</span>
						</div>

						<Dropdown className="dropdown-btn menu-icon me-2" drop="down">
							<Dropdown.Toggle className="btn-fill spaces min-w-120">
								Xem dạng: {viewType}
								<i className="bi bi-caret-down-fill spaces pl-4 pr-0 mr-0 pt-2"></i>
							</Dropdown.Toggle>

							<Dropdown.Menu className="dropdown-actions">
								<Dropdown.Item onClick={() => setViewType(VIEW_TYPE.TABLE)}>
									<div className="ps-5 spaces line-height-30">Bảng</div>
								</Dropdown.Item>
								<Dropdown.Item onClick={() => setViewType(VIEW_TYPE.TAGS)}>
									<div className="ps-5 spaces line-height-30">Thẻ</div>
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>

						<Button className="btn-fill spaces min-w-80" onClick={onExportCheckinXML}>
							Xuất XML Check-in
						</Button>
						<Button className="btn-fill spaces min-w-80" onClick={onExportXML}>
							Xuất XML
						</Button>
						<Button className="btn-fill spaces min-w-80" onClick={onValidateXML}>
							Validate XML
						</Button>
						<Button className="btn-fill spaces min-w-80 me-2" onClick={onDownloadXML}>
							Tải XML
						</Button>

						<Button
							className="btn-fill"
							onClick={() => {
								onSendXML('checkin');
							}}
						>
							Gửi CheckIn
						</Button>
						<Button
							className="btn-fill"
							onClick={() => {
								onSendXML('payment');
							}}
						>
							Gửi XML-130/QĐ-BYT
						</Button>
					</div>
				</Modal.Header>
				<Modal.Body className="py-6 px-0 spaces overflow-hidden spaces h-calc-vh-90 bg-white">
					<CustomTabMenu danhsachTabs={dsTabs} />
				</Modal.Body>
			</div>
		</Modal>
	);
};

export default XmlPreview;
